"use client";
import React, { createContext, useContext, useEffect, useMemo, useCallback, useState } from "react";
import { AdvancedStateManager } from "@/managers/stateManagement/AdvancedStateManager";
import { EntityManager } from "@/managers/EntityManager";
import { StateMachineTool } from "@/managers/stateManagement/StateMachineTool";
import { IStateMachineTool } from "@/interfaces/stateManagement/StateMachineToolInterface";
import {
    StateCommand,
    StateTrigger,
    CommandExecutionResult,
    TriggerExecutionResult,
} from "@/interfaces/stateManagement/CommandInterfaces";
import {
    SystemState,
    EntityWithDependencies,
    StateChangeEvent,
} from "@/interfaces/stateManagement/StateManagerInterfaces";
import { EntityType } from "@/interfaces/entities/EntityType";
import { MetaDataInterface } from "@/interfaces/entities/entity_blueprints/MetaDataInterface";

/**
 * State Manager Context Value
 */
interface StateManagerContextValue {
    /** State manager instance */
    stateManager: AdvancedStateManager;

    /** State machine tool instance - provides all tool methods */
    tool: IStateMachineTool;

    /** Entity manager instance (for advanced usage) */
    entityManager: EntityManager;

    /** Current system state */
    systemState: SystemState;

    /** Execute a single command */
    executeCommand: (command: StateCommand) => Promise<CommandExecutionResult>;

    /** Execute a trigger (chain of commands) */
    executeTrigger: (trigger: StateTrigger) => Promise<TriggerExecutionResult>;

    /** Reset full state */
    resetState: (metadata?: MetaDataInterface) => void;

    /** Update metadata only */
    setMetadata: (metadata?: MetaDataInterface) => void;

    /** Get entity with dependencies */
    getEntityWithDependencies: (entityId: string, entityType: EntityType) => EntityWithDependencies | null;

    /** Check if entity can be deleted */
    canDeleteEntity: (entityId: string, entityType: EntityType) => boolean;

    /** Get current system state */
    getSystemState: () => SystemState;

    /** Loading state */
    isLoading: boolean;

    /** Last error */
    lastError: string | null;

    /** Recent state changes */
    recentEvents: StateChangeEvent[];
}

export const StateManagerContext = createContext<StateManagerContextValue | undefined>(undefined);

/**
 * State Manager Provider
 */
export const StateManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Create singleton instances
    const entityManager = useMemo(() => new EntityManager(), []);
    const stateManager = useMemo(() => new AdvancedStateManager(entityManager), [entityManager]);
    const tool = useMemo(() => new StateMachineTool(stateManager, entityManager), [stateManager, entityManager]);

    // State
    const [systemState, setSystemState] = useState<SystemState>(() => stateManager.getSystemState());
    const [isLoading, setIsLoading] = useState(false);
    const [lastError, setLastError] = useState<string | null>(null);
    const [recentEvents, setRecentEvents] = useState<StateChangeEvent[]>([]);

    // Subscribe to state changes
    useEffect(() => {
        const unsubscribe = stateManager.subscribe((event: StateChangeEvent) => {
            // Update system state when changes occur
            setSystemState(stateManager.getSystemState());

            // Add to recent events (keep last 50)
            setRecentEvents(prev => {
                const newEvents = [event, ...prev];
                return newEvents.slice(0, 50);
            });
        });

        return unsubscribe;
    }, [stateManager]);

    /**
     * Execute a single command
     */
    const executeCommand = useCallback(async (command: StateCommand): Promise<CommandExecutionResult> => {
        setIsLoading(true);
        setLastError(null);

        try {
            const result = await stateManager.executeCommand(command);

            if (!result.success) {
                setLastError(result.error || 'Command execution failed');
            }

            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setLastError(errorMessage);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [stateManager]);

    /**
     * Execute a trigger (chain of commands)
     */
    const executeTrigger = useCallback(async (trigger: StateTrigger): Promise<TriggerExecutionResult> => {
        setIsLoading(true);
        setLastError(null);

        try {
            const result = await stateManager.executeTrigger(trigger);

            if (!result.success) {
                setLastError(result.errors?.join('; ') || 'Trigger execution failed');
            }

            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setLastError(errorMessage);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [stateManager]);

    /**
     * Get entity with dependencies
     */
    const getEntityWithDependencies = useCallback(
        (entityId: string, entityType: EntityType): EntityWithDependencies | null => {
            return stateManager.getEntityWithDependencies(entityId, entityType);
        },
        [stateManager]
    );

    /**
     * Check if entity can be deleted
     */
    const canDeleteEntity = useCallback(
        (entityId: string, entityType: EntityType): boolean => {
            const entityWithDeps = stateManager.getEntityWithDependencies(entityId, entityType);
            return entityWithDeps?.canDelete ?? false;
        },
        [stateManager]
    );

    /**
     * Get current system state
     */
    const getSystemState = useCallback((): SystemState => {
        return stateManager.getSystemState();
    }, [stateManager]);

    const resetState = useCallback((metadata?: MetaDataInterface) => {
        stateManager.resetState(metadata);
    }, [stateManager]);

    const updateMetadata = useCallback((metadata?: MetaDataInterface) => {
        stateManager.setMetadata(metadata);
    }, [stateManager]);

    const value = useMemo<StateManagerContextValue>(
        () => ({
            stateManager,
            tool,
            entityManager,
            systemState,
            executeCommand,
            executeTrigger,
            resetState,
            setMetadata: updateMetadata,
            getEntityWithDependencies,
            canDeleteEntity,
            getSystemState,
            isLoading,
            lastError,
            recentEvents,
        }),
        [
            stateManager,
            tool,
            entityManager,
            systemState,
            executeCommand,
            executeTrigger,
            resetState,
            updateMetadata,
            getEntityWithDependencies,
            canDeleteEntity,
            getSystemState,
            isLoading,
            lastError,
            recentEvents,
        ]
    );

    return <StateManagerContext.Provider value={value}>{children}</StateManagerContext.Provider>;
};

/**
 * Hook to use State Manager Context
 */
export const useStateManager = (): StateManagerContextValue => {
    const context = useContext(StateManagerContext);
    if (context === undefined) {
        throw new Error("useStateManager must be used within a StateManagerProvider");
    }
    return context;
};

